/**
 * Formats a date using Intl.DateTimeFormat
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @param locale - Locale string (defaults to 'en-US')
 * @returns Formatted date string
 * @example
 * formatDate(new Date()) // "Jan 15, 2024"
 * formatDate(new Date(), { hour: '2-digit', minute: '2-digit' }, 'en-GB') // "15:30"
 */
export function formatDate(
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  locale: string = 'en-US'
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, options).format(d);
}

/**
 * Formats a date as relative time (e.g., "2h ago", "in 5m")
 * @param date - Date string or Date object
 * @param options - Options for formatting
 * @param options.locale - Locale string (defaults to 'en-US')
 * @returns Relative time string or formatted date for older dates
 * @example
 * formatRelativeTime(new Date(Date.now() - 3600000)) // "1h ago"
 * formatRelativeTime(new Date(Date.now() + 3600000)) // "in 1h"
 */
export function formatRelativeTime(
  date: string | Date,
  options: { locale?: string } = {}
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);
  
  // Handle future dates
  if (diffInSeconds < 0) {
    const absDiff = Math.abs(diffInSeconds);
    if (absDiff < 60) return 'in a few seconds';
    if (absDiff < 3600) return `in ${Math.floor(absDiff / 60)}m`;
    if (absDiff < 86400) return `in ${Math.floor(absDiff / 3600)}h`;
    return formatDate(d, {}, options.locale);
  }

  // Handle past dates
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return formatDate(d, {}, options.locale);
}
